import { getCurrentLayer, getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { moveNode, selectNode, setConditionalVariationMode } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { BuildComponentKey } from "@common/build/types";
import { NodeApi, Tree } from "react-arborist";
import "./style.scss";
import { useEffect, useRef, useState } from "react";
import RenderConditionalVariationTreeNode from "./ConditionalVariationTreeNode";
import OliverText, { TextType } from "@builder/components/general/OliverText";
import { getUIComponent } from "@builder/store/slices/BuilderSlice/builderMap";
import { useTranslation } from "react-i18next";
import useFormatCondition from "@builder/hooks/useFormatCondition";
import classNames from "classnames";
import { checkSmartTileAllowed } from "../../OliverBuildFrame/RenderVariationNodes";

export type VariationTreeNode = {
    id: string;
    name: string;
    customName?: string;
    icon: string;
    children?: VariationTreeNode[];
    hasChildren: boolean;
    isOpen?: boolean;
    variationIndex?: number;
};

const TREE_NODE_HEIGHT = 32;

const childOfNode = (node: VariationTreeNode, nodeId: string): boolean => {
    if (node.id === nodeId) {
        return true;
    }

    if (node.children) {
        return node.children.some(child => childOfNode(child, nodeId));
    }

    return false;
};

const getTreeHeight = (tree: VariationTreeNode[]): number => {
    let treeNodes = 0;

    const traverseTree = (node: VariationTreeNode) => {
        treeNodes++;
        if (node.children && node.isOpen) {
            node.children.forEach(traverseTree);
        }
    };

    tree.forEach(traverseTree);

    return treeNodes * TREE_NODE_HEIGHT;
};

const updateNodeRecursive = (node: VariationTreeNode, id: string): VariationTreeNode => {
    if (node.id === id) {
        return { ...node, isOpen: !node.isOpen };
    }
    return {
        ...node,
        children: node.children?.map(child => updateNodeRecursive(child, id)) ?? []
    };
};

const applyCollapse = (nodes: VariationTreeNode[]): VariationTreeNode[] => {
    return nodes.map(node => {
        return {
            ...node,
            children: node.children ? applyCollapse(node.children) : [],
            isOpen: node.isOpen ?? true
        };
    });
};

const getInitialOpenState = (nodes: VariationTreeNode[]): Record<string, boolean> => {
    const openState: Record<string, boolean> = {};

    const traverse = (node: VariationTreeNode) => {
        openState[node.id] = node.isOpen ?? true;
        node.children?.forEach(traverse);
    };

    nodes.forEach(traverse);

    return openState;
};

const ConditionalVariationTree = () => {
    const dispatch = useAppDispatch();
    const conditionalVariationMode = useAppSelector(state => state.builder.conditionalVariationMode);
    const currentLayer = useAppSelector(getCurrentLayer);
    const currentComponents = currentLayer?.components ?? {};
    const { t } = useTranslation();
    const node = conditionalVariationMode?.id ? currentComponents[conditionalVariationMode.id] : null;
    const nodeUI = node?.name ? getUIComponent(node.name as BuildComponentKey) : null;
    const [variantTrees, setVariantTrees] = useState<VariationTreeNode[][]>([]);
    const [variationIndexMap, setVariationIndexMap] = useState<number[]>([]); // Maps tree index to original variation index
    const [treeHeights, setTreeHeights] = useState<number[]>([]);
    const [treeCollapsed, setTreeCollapsed] = useState<boolean[]>([]);
    const formatCondition = useFormatCondition();
    const selectedNode = useAppSelector(getSelectedNode);
    const treeRefs = useRef<any[]>([]);

    const buildTreeNode = (nodeId: string, variationIndex?: number): VariationTreeNode | null => {
        const node = currentComponents[nodeId];
        if (!node) {
            return null;
        }
        const nodeUI = node?.name ? getUIComponent(node.name as BuildComponentKey) : null;
        return {
            id: nodeId,
            name: node?.name || "",
            icon: nodeUI?.icon ?? "",
            children: node?.children?.map(child => buildTreeNode(child)).filter((child): child is VariationTreeNode => child !== null) ?? [],
            hasChildren: (node?.children?.length ?? 0) > 0,
            isOpen: true,
            variationIndex
        };
    };

    // node?.conditionalVariations?.map(variation => {
    //     return variation?.children?.map(child => buildTreeNode(child)) ?? [];
    // }) ?? []

    useEffect(() => {
        const trees: VariationTreeNode[][] = [];
        const indexMap: number[] = [];

        node?.conditionalVariations?.forEach((variation, originalIndex) => {
            if (checkSmartTileAllowed(node, variation, currentLayer)) {
                const treeNodes =
                    variation?.children
                        ?.map(child => buildTreeNode(child, originalIndex))
                        .filter((child): child is VariationTreeNode => child !== null) ?? [];
                trees.push(treeNodes);
                indexMap.push(originalIndex);
            }
        });

        setVariantTrees(trees);
        setVariationIndexMap(indexMap);
    }, [node, currentComponents]);

    useEffect(() => {
        setTreeHeights(variantTrees.map(getTreeHeight));
    }, [variantTrees]);

    const undoConditionalVariationMode = () => {
        dispatch(selectNode(conditionalVariationMode?.id || null));
        dispatch(setConditionalVariationMode({ id: null, index: null }));
    };

    const selectConditionalVariation = (index: number) => {
        dispatch(selectNode(conditionalVariationMode?.id));
        dispatch(setConditionalVariationMode({ id: conditionalVariationMode?.id, index }));
    };

    const handleParentCollapse = (index: number) => {
        setTreeCollapsed(prev => {
            const newCollapsed = [...prev];
            newCollapsed[index] = !newCollapsed[index];
            return newCollapsed;
        });
    };

    const onMove = (args: { dragIds: string[]; parentId: string | null; index: number }) => {
        if (args.parentId) {
            dispatch(moveNode({ nodeId: args.dragIds[0], targetId: args.parentId || "", index: args.index }));
        } else {
            dispatch(
                moveNode({
                    nodeId: args.dragIds[0],
                    targetId: `${conditionalVariationMode?.id}|${variantTrees.findIndex(tree =>
                        tree.find(node => childOfNode(node, args.dragIds[0]))
                    )}`,
                    index: args.index
                })
            );
        }
    };

    const handleToggle = (treeIndex: number, nodeApi: NodeApi<VariationTreeNode>) => {
        setVariantTrees(prev => {
            const updated = [...prev];
            if (updated[treeIndex]) {
                updated[treeIndex] = updated[treeIndex].map(node => updateNodeRecursive(node, nodeApi.data.id));
            }
            return updated;
        });
        nodeApi.toggle();

        setTimeout(() => {
            const treeRef = treeRefs.current[treeIndex];
            if (treeRef?.visibleNodes) {
                setTreeHeights(prev => {
                    const newHeights = [...prev];
                    newHeights[treeIndex] = treeRef.visibleNodes.length * TREE_NODE_HEIGHT;
                    return newHeights;
                });
            }
        }, 10);
    };

    if (!node) return null;

    return (
        <div className="conditional-variation-mode">
            <button className="cv-mode-button" onClick={undoConditionalVariationMode}>
                <img src="/arrow-right-long.svg" alt="Conditional Variation Mode" />
                Return to build
            </button>
            <div className="cv-header-row">
                <div className="cv-header-row-container">
                    <div className="cv-header-row-node-info">
                        <img src={nodeUI?.icon} />
                        <OliverText type={TextType.BODY_4} text={node.name} translatePrefix="builderElements." />
                    </div>
                    <img src="/fork.svg" />
                </div>
                <div className="cv-header-row-variant-count">
                    <OliverText type={TextType.BODY_4} color="blue" text={`${node.conditionalVariations?.length} ${t("sidebarCollapse.variants")}`} />
                </div>
            </div>
            <div className="cv-trees">
                {variantTrees.map((variantTree, treeIndex) => {
                    const variationIndex = variationIndexMap[treeIndex] ?? variantTree[0]?.variationIndex ?? treeIndex;
                    const variation = node?.conditionalVariations?.[variationIndex];

                    return (
                        <div key={treeIndex} className="cv-tree-wrapper">
                            <button className="cv-tree-toggle-button" onClick={() => handleParentCollapse(treeIndex)}>
                                <img src="/angled-arrow-down.svg" />
                            </button>
                            <div
                                className={classNames("cv-tree-container", {
                                    ["selected"]:
                                        selectedNode?.id === conditionalVariationMode?.id && variationIndex === conditionalVariationMode?.index
                                })}
                                onClick={() => selectConditionalVariation(variationIndex)}
                            >
                                <div className="cv-tree-header">
                                    <img src="/fork.svg" />
                                    <OliverText type={TextType.BODY_4} text={formatCondition(variation?.condition || "")} />
                                </div>
                                {!treeCollapsed[treeIndex] && variantTree.length > 0 && (
                                    <Tree
                                        key={treeIndex}
                                        data={applyCollapse(variantTree)}
                                        width={200}
                                        height={treeHeights[treeIndex] ?? TREE_NODE_HEIGHT}
                                        indent={15}
                                        rowHeight={TREE_NODE_HEIGHT}
                                        ref={el => (treeRefs.current[treeIndex] = el)}
                                        onMove={onMove}
                                        openByDefault={false}
                                        initialOpenState={getInitialOpenState(variantTree)}
                                    >
                                        {({ node, dragHandle, ...rest }) => (
                                            <RenderConditionalVariationTreeNode
                                                node={node}
                                                toggleCollapse={handleToggle}
                                                dragHandle={dragHandle}
                                                rest={rest}
                                                treeIndex={treeIndex}
                                            />
                                        )}
                                    </Tree>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ConditionalVariationTree;
